/**
 * Tailwind flex-wrap 关键词云 — 替代 echarts-wordcloud 的算法布局,
 * 用纯 CSS inline-flex 保证水平对齐 + grid-like 排版。
 *
 * 颜色:正面 emerald-500 / 负面 rose-500 / 中性 slate-500
 * 字号:14-26px linear scale by weight desc
 * 字重:>= max*0.5 的 600,其余 400
 */
import { useMemo } from 'react';

import { useBreakpoint } from '@/lib/breakpoints';

export interface CloudWord {
  word: string;
  weight: number; // 任意正数,驱动 size + 排序
  polarity?: number; // -1 ~ 1, 驱动颜色
}

interface KeywordCloudProps {
  words: CloudWord[];
  defaultPolarity?: number;
  className?: string;
  minSize?: number;
  maxSize?: number;
}

function colorOf(polarity: number | undefined, fallback: number | undefined): string {
  const p = polarity ?? fallback ?? 0;
  if (p > 0.3) return '#10B981'; // emerald-500
  if (p < -0.3) return '#F43F5E'; // rose-500
  return '#64748B'; // slate-500
}

function chipClassOf(polarity: number | undefined, fallback: number | undefined): string {
  const p = polarity ?? fallback ?? 0;
  if (p > 0.3) return 'bg-emerald-50';
  if (p < -0.3) return 'bg-rose-50';
  return 'bg-slate-50';
}

export default function KeywordCloud({
  words,
  defaultPolarity,
  className = '',
  minSize,
  maxSize,
}: KeywordCloudProps) {
  const { isMobile } = useBreakpoint();
  // 9.2f: mobile 缩字号防溢出(未显式传 props 时);桌面观感留 Issue 2,不动
  const effMinSize = minSize ?? (isMobile ? 12 : 14);
  const effMaxSize = maxSize ?? (isMobile ? 20 : 26);
  const sorted = useMemo(() => [...words].sort((a, b) => b.weight - a.weight), [words]);
  const { minW, maxW } = useMemo(() => {
    if (!sorted.length) return { minW: 0, maxW: 0 };
    return {
      minW: sorted[sorted.length - 1].weight,
      maxW: sorted[0].weight,
    };
  }, [sorted]);

  if (!sorted.length) {
    return (
      <div className={`flex items-center justify-center text-[12px] text-slate-400 p-4 ${className}`}>
        暂无关键词
      </div>
    );
  }

  const sizeOf = (w: number): number => {
    if (maxW === minW) return Math.round((effMinSize + effMaxSize) / 2);
    return Math.round(effMinSize + ((w - minW) / (maxW - minW)) * (effMaxSize - effMinSize));
  };
  const boldThreshold = maxW * 0.5;

  return (
    <div
      className={`flex flex-wrap items-baseline justify-start gap-x-3 gap-y-2 p-4 ${className}`}
      style={{ fontFamily: 'PingFang SC, Microsoft YaHei, sans-serif' }}
    >
      {sorted.map((w) => (
        <span
          key={w.word}
          className={`px-2 py-1 rounded-md ${chipClassOf(w.polarity, defaultPolarity)}`}
          style={{
            fontSize: `${sizeOf(w.weight)}px`,
            color: colorOf(w.polarity, defaultPolarity),
            fontWeight: w.weight >= boldThreshold ? 600 : 400,
            lineHeight: 1.3,
          }}
        >
          {w.word}
        </span>
      ))}
    </div>
  );
}
